import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { riderApi } from '../../services/riderApi';

export interface Rider {
    id: number;
    name: string;
    email: string;
    loginID: string;
    type: string;
    status: number;
    branch_id?: number;
    created_at: string;
    updated_at: string;
    profile?: {
        id: number;
        user_id: number;
        cnic: string;
        gender: string;
        phone: string;
        phone2?: string;
        dob: string;
        language?: string;
        city_id: number;
        address?: string;
        city?: {
            id: number;
            name: string;
            code: string;
        };
    };
    rider?: {
        id: number;
        user_id: number;
        capacity_in_unit?: string;
        capacity_in_weight?: string;
        capacity_in_volume?: string;
        vehicle_number?: string;
        pin_code: string;
        weekly_of_day?: string;
        distance_allowed?: string;
        license_validity?: string;
        shift_timing_from?: string;
        shift_timing_to?: string;
        break_timing_from?: string;
        break_timing_to?: string;
    };
    branch?: {
        id: number;
        name: string;
        code: string;
    };
}

export interface CreateRiderData {
    name: string;
    email: string;
    password: string;
    cnic: string;
    gender: string;
    phone: string;
    phone2?: string;
    dob: string;
    language?: string;
    city_id: number;
    address?: string;
    capacity_in_unit?: string;
    capacity_in_weight?: string;
    capacity_in_volume?: string;
    vehicle_number?: string;
    weekly_of_day?: string;
    distance_allowed?: string;
    license_validity?: string;
    shift_timing_from?: string;
    shift_timing_to?: string;
    break_timing_from?: string;
    break_timing_to?: string;
}

export interface UpdateRiderData {
    name?: string;
    email?: string;
    password?: string;
    cnic?: string;
    gender?: string;
    phone?: string;
    phone2?: string;
    dob?: string;
    language?: string;
    city_id?: number;
    address?: string;
    capacity_in_unit?: string;
    capacity_in_weight?: string;
    capacity_in_volume?: string;
    vehicle_number?: string;
    weekly_of_day?: string;
    distance_allowed?: string;
    license_validity?: string;
    shift_timing_from?: string;
    shift_timing_to?: string;
    break_timing_from?: string;
    break_timing_to?: string;
}

export interface RiderFilters {
    query?: string;
    city_id?: number;
    status?: string;
}

export interface RiderResponse {
    riders: Rider[];
    pagination: {
        current_page: number;
        last_page: number;
        per_page: number;
        total: number;
        from: number;
        to: number;
    };
}

export interface City {
    id: number;
    name: string;
    code: string;
}

interface RiderState {
    riders: Rider[];
    cities: City[];
    selectedRider: Rider | null;
    pagination: {
        current_page: number;
        last_page: number;
        per_page: number;
        total: number;
        from: number;
        to: number;
    } | null;
    filters: RiderFilters;
    loading: boolean;
    error: string | null;
}

const initialState: RiderState = {
    riders: [],
    cities: [],
    selectedRider: null,
    pagination: null,
    filters: {},
    loading: false,
    error: null,
};

// Async thunks
export const getRiders = createAsyncThunk(
    'riders/getRiders',
    async (params?: { page?: number; per_page?: number; filters?: RiderFilters }) => {
        const response = await riderApi.getRiders(params);
        return response;
    }
);

export const getRider = createAsyncThunk(
    'riders/getRider',
    async (id: number) => {
        const response = await riderApi.getRider(id);
        return response;
    }
);

export const createRider = createAsyncThunk(
    'riders/createRider',
    async (data: CreateRiderData) => {
        const response = await riderApi.createRider(data);
        return response;
    }
);

export const updateRider = createAsyncThunk(
    'riders/updateRider',
    async ({ id, data }: { id: number; data: UpdateRiderData }) => {
        const response = await riderApi.updateRider(id, data);
        return response;
    }
);

export const deleteRider = createAsyncThunk(
    'riders/deleteRider',
    async (id: number) => {
        await riderApi.deleteRider(id);
        return id;
    }
);

export const toggleRiderStatus = createAsyncThunk(
    'riders/toggleRiderStatus',
    async (id: number) => {
        const response = await riderApi.toggleRiderStatus(id);
        return response;
    }
);

export const getCities = createAsyncThunk(
    'riders/getCities',
    async () => {
        const response = await riderApi.getCities();
        return response;
    }
);

const riderSlice = createSlice({
    name: 'riders',
    initialState,
    reducers: {
        setFilters: (state, action: PayloadAction<RiderFilters>) => {
            state.filters = { ...state.filters, ...action.payload };
        },
        clearFilters: (state) => {
            state.filters = {};
        },
        setSelectedRider: (state, action: PayloadAction<Rider | null>) => {
            state.selectedRider = action.payload;
        },
        clearError: (state) => {
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        // getRiders
        builder
            .addCase(getRiders.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(getRiders.fulfilled, (state, action) => {
                state.loading = false;
                state.riders = action.payload.riders;
                state.pagination = action.payload.pagination;
            })
            .addCase(getRiders.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message || 'Failed to fetch riders';
            });

        // getRider
        builder
            .addCase(getRider.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(getRider.fulfilled, (state, action) => {
                state.loading = false;
                state.selectedRider = action.payload.rider;
            })
            .addCase(getRider.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message || 'Failed to fetch rider';
            });

        // createRider
        builder
            .addCase(createRider.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(createRider.fulfilled, (state, action) => {
                state.loading = false;
                state.riders.unshift(action.payload.rider);
                if (state.pagination) {
                    state.pagination.total += 1;
                }
            })
            .addCase(createRider.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message || 'Failed to create rider';
            });

        // updateRider
        builder
            .addCase(updateRider.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(updateRider.fulfilled, (state, action) => {
                state.loading = false;
                const index = state.riders.findIndex(r => r.id === action.payload.rider.id);
                if (index !== -1) {
                    state.riders[index] = action.payload.rider;
                }
                if (state.selectedRider?.id === action.payload.rider.id) {
                    state.selectedRider = action.payload.rider;
                }
            })
            .addCase(updateRider.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message || 'Failed to update rider';
            });

        // deleteRider
        builder
            .addCase(deleteRider.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(deleteRider.fulfilled, (state, action) => {
                state.loading = false;
                state.riders = state.riders.filter(r => r.id !== action.payload);
                if (state.selectedRider?.id === action.payload) {
                    state.selectedRider = null;
                }
                if (state.pagination) {
                    state.pagination.total -= 1;
                }
            })
            .addCase(deleteRider.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message || 'Failed to delete rider';
            });

        // toggleRiderStatus
        builder
            .addCase(toggleRiderStatus.pending, (state) => {
                state.error = null;
            })
            .addCase(toggleRiderStatus.fulfilled, (state, action) => {
                const index = state.riders.findIndex(r => r.id === action.payload.rider.id);
                if (index !== -1) {
                    state.riders[index] = action.payload.rider;
                }
                if (state.selectedRider?.id === action.payload.rider.id) {
                    state.selectedRider = action.payload.rider;
                }
            })
            .addCase(toggleRiderStatus.rejected, (state, action) => {
                state.error = action.error.message || 'Failed to toggle rider status';
            });

        // getCities
        builder
            .addCase(getCities.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(getCities.fulfilled, (state, action) => {
                state.loading = false;
                state.cities = action.payload;
            })
            .addCase(getCities.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message || 'Failed to fetch cities';
            });
    },
});

export const { setFilters, clearFilters, setSelectedRider, clearError } = riderSlice.actions;
export default riderSlice.reducer;
